import {
  WaveCloudAPI,
  type WaveApiConfig,
  type WaveRealtimeStatus,
  type WaveSaveState,
  type WaveSaveStateMeta,
  WAVE_APP_ID,
  emptyWaveSaveStateMeta,
} from "@vida/wave-core";
import { getAppsyncEndpoint } from "../config";
import { getIdToken as authGetIdToken } from "../auth";
import { WAVE_AGENT_MODEL_ID } from "./waveAgentModel";

export function createWaveApiConfig(): WaveApiConfig {
  return {
    endpoint: getAppsyncEndpoint(),
    getIdToken: authGetIdToken,
  };
}

let realtimeStatus: WaveRealtimeStatus = "offline";
let syncEmitError: string | null = null;
let waveSaveState: WaveSaveState = "synced";
let waveSaveStateMeta: WaveSaveStateMeta = emptyWaveSaveStateMeta();
const realtimeStatusListeners = new Set<() => void>();
const syncEmitErrorListeners = new Set<() => void>();
const waveSaveStateListeners = new Set<() => void>();

export function subscribeRealtimeStatus(listener: () => void): () => void {
  realtimeStatusListeners.add(listener);
  return () => realtimeStatusListeners.delete(listener);
}

export function getRealtimeStatus(): WaveRealtimeStatus {
  return realtimeStatus;
}

export function setRealtimeStatus(status: WaveRealtimeStatus): void {
  if (realtimeStatus === status) return;
  realtimeStatus = status;
  for (const listener of realtimeStatusListeners) listener();
}

export function subscribeSyncEmitError(listener: () => void): () => void {
  syncEmitErrorListeners.add(listener);
  return () => syncEmitErrorListeners.delete(listener);
}

export function getSyncEmitError(): string | null {
  return syncEmitError;
}

export function setSyncEmitError(message: string | null): void {
  if (syncEmitError === message) return;
  syncEmitError = message;
  for (const listener of syncEmitErrorListeners) listener();
}

export function subscribeWaveSaveState(listener: () => void): () => void {
  waveSaveStateListeners.add(listener);
  return () => waveSaveStateListeners.delete(listener);
}

export function getWaveSaveState(): WaveSaveState {
  return waveSaveState;
}

export function getWaveSaveStateMeta(): WaveSaveStateMeta {
  return waveSaveStateMeta;
}

export function setWaveSaveState(
  state: WaveSaveState,
  meta: WaveSaveStateMeta,
): void {
  if (
    waveSaveState === state &&
    waveSaveStateMeta.pendingOutboundCount === meta.pendingOutboundCount &&
    waveSaveStateMeta.lastAckAt === meta.lastAckAt &&
    waveSaveStateMeta.lastError === meta.lastError
  ) {
    return;
  }
  waveSaveState = state;
  waveSaveStateMeta = meta;
  for (const listener of waveSaveStateListeners) listener();
}

export function resetWaveSaveState(): void {
  setWaveSaveState("synced", emptyWaveSaveStateMeta());
}

export async function ensureWaveCloudProfile(): Promise<void> {
  if (!getAppsyncEndpoint()) return;
  try {
    await WaveCloudAPI.ensureProfile(createWaveApiConfig());
  } catch {
    // Non bloccare la shell se il profilo è già registrato o la rete fallisce.
  }
}

export async function runWaveAssistant(
  waveId: string,
  instruction: string,
): Promise<{ jobId: string }> {
  if (!instruction.trim()) throw new Error("Istruzione vuota.");
  const cfg = createWaveApiConfig();
  const params = {
    bedrock: {
      systemText:
        "Sei l'assistente Wave. Rispondi in italiano, sintetico, con action item.",
      userText: `Wave ${waveId}: ${instruction}`,
      maxTokens: 2048,
    },
    waveId,
  };
  const result = await WaveCloudAPI.submitAIJob(cfg, {
    kind: "BEDROCK_CONVERSE",
    provider: "BEDROCK",
    prompt: instruction,
    paramsJson: JSON.stringify(params),
    inputAssetKeys: [],
    appId: WAVE_APP_ID,
    modelId: WAVE_AGENT_MODEL_ID,
    feature: "wave.agent.draft",
  });
  return { jobId: result.jobId };
}

export type ActivityKind =
  | "cover"
  | "assistant"
  | "upload"
  | "sync"
  | "share"
  | "general";

export type ActivityItem = {
  id: string;
  title: string;
  detail: string;
  at: number;
  kind: ActivityKind;
  completedAt?: number | null;
};

export function activityKindLabel(kind: ActivityKind): string {
  switch (kind) {
    case "cover":
      return "Cover AI";
    case "assistant":
      return "Assistente";
    case "upload":
      return "Allegati";
    case "sync":
      return "Sync";
    case "share":
      return "Condivisione";
    default:
      return "Attività";
  }
}

function inferActivityKind(title: string): ActivityKind {
  const normalized = title.toLowerCase();
  if (normalized.includes("cover")) return "cover";
  if (normalized.includes("assistente") || normalized.includes("documenti")) return "assistant";
  if (normalized.includes("allegat")) return "upload";
  if (normalized.includes("sync") || normalized.includes("presenza")) return "sync";
  if (normalized.includes("condivisione") || normalized.includes("link")) return "share";
  return "general";
}

export function isActivityPending(item: ActivityItem): boolean {
  return item.detail.includes("…") && !item.completedAt;
}

export function activityElapsedLabel(item: ActivityItem, now = Date.now()): string {
  const start = item.at;
  const end = item.completedAt ?? now;
  const seconds = Math.max(1, Math.round((end - start) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rem = seconds % 60;
  return rem > 0 ? `${minutes}m ${rem}s` : `${minutes}m`;
}

const activityLog: ActivityItem[] = [];
const activityListeners = new Set<() => void>();

/** Stable empty snapshot for useSyncExternalStore (must not allocate per call). */
export const EMPTY_ACTIVITY: ActivityItem[] = [];

let activitySnapshot: ActivityItem[] = EMPTY_ACTIVITY;

function syncActivitySnapshot(): void {
  activitySnapshot =
    activityLog.length === 0 ? EMPTY_ACTIVITY : [...activityLog];
}

export function trackActivity(title: string, detail: string, kind?: ActivityKind): void {
  const resolvedKind = kind ?? inferActivityKind(title);
  const isPending = detail.includes("…");

  if (!isPending) {
    const pending = activityLog.find(
      (item) => item.title === title && isActivityPending(item),
    );
    if (pending) {
      pending.detail = detail;
      pending.completedAt = Date.now();
      syncActivitySnapshot();
      for (const l of activityListeners) l();
      return;
    }
  }

  activityLog.unshift({
    id: crypto.randomUUID(),
    title,
    detail,
    at: Date.now(),
    kind: resolvedKind,
    completedAt: isPending ? null : Date.now(),
  });
  if (activityLog.length > 20) activityLog.length = 20;
  syncActivitySnapshot();
  for (const l of activityListeners) l();
}

export function subscribeActivity(listener: () => void): () => void {
  activityListeners.add(listener);
  return () => activityListeners.delete(listener);
}

export function getActivityLog(): ActivityItem[] {
  return activitySnapshot;
}
